// The full-screen frames either side of the questions: the per-section welcome,
// the one-time directions page, and the Save and Finish Later confirmation.
//
// They share the question screen's chrome -- a black bar with a title, an
// `Instructions` heading, body copy, and the action buttons bottom-right --
// because that is how the real test presents them (reference screenshots 01,
// 02, 03 and 14).
import React from 'react';

type FrameProps = {
    barTitle: string;
    heading?: string;
    children: React.ReactNode;
    // Rendered bottom-left; the directions page is the only frame with one.
    secondaryLabel?: string;
    onSecondary?: () => void;
    primaryLabel: string;
    onPrimary: () => void;
    primaryDisabled?: boolean;
};

export function TSIA2Frame({
    barTitle, heading, children,
    secondaryLabel, onSecondary,
    primaryLabel, onPrimary, primaryDisabled,
}: FrameProps) {
    return (
        <div className='tsia2-shell'>
            {/* No tool icons on a frame -- the toolbar belongs to questions. */}
            <div className='tsia2-bar'>
                <span className='tsia2-bar-title'>{barTitle}</span>
            </div>
            <div className='tsia2-body'>
                {heading && <h2 className='tsia2-heading'>{heading}</h2>}
                <div className='tsia2-frame-copy'>{children}</div>
            </div>
            <div className='tsia2-footer'>
                {secondaryLabel && (
                    <button
                        type='button'
                        className='tsia2-button tsia2-button-secondary'
                        onClick={onSecondary}
                    >
                        {secondaryLabel}
                    </button>
                )}
                <button
                    type='button'
                    className='tsia2-button tsia2-button-primary'
                    onClick={onPrimary}
                    disabled={primaryDisabled}
                >
                    {primaryLabel}
                </button>
            </div>
        </div>
    );
}

export function TSIA2Welcome({ prompt, onNext }: { prompt: string | null; onNext: () => void }) {
    return (
        <TSIA2Frame barTitle='Welcome' heading='Instructions' primaryLabel='Next' onPrimary={onNext}>
            <p>{prompt}</p>
        </TSIA2Frame>
    );
}

// Shown once, before the first section. The numbered list mirrors the real
// test's "Directions for Answering Questions" page.
export function TSIA2Directions({ onBack, onStart }: { onBack: () => void; onStart: () => void }) {
    return (
        <TSIA2Frame
            barTitle='Directions for Answering Questions'
            heading='Instructions'
            secondaryLabel='Back'
            onSecondary={onBack}
            primaryLabel='Start Test Session'
            onPrimary={onStart}
        >
            <ol className='tsia2-directions'>
                <li>Read each question carefully before answering.</li>
                <li>Select the best answer from the choices given.</li>
                <li>If a passage or question is longer than the screen, scroll down to read all of it.</li>
                <li>Click the circle next to your answer, then click <b>Submit</b>.</li>
                <li>
                    After you click <b>Submit</b> you will be asked to confirm your selection. Once
                    you confirm, you cannot return to that question.
                </li>
                <li>
                    Some questions offer a calculator. When one is available, a <b>Calculator</b>{' '}
                    button appears in the toolbar. The <b>Highlighter</b> is available throughout.
                </li>
                <li>Start the test by clicking <b>Start Test Session</b>.</li>
            </ol>
            <p className='tsia2-note'>
                <b>NOTE:</b> Do not refresh this page or open other tabs during the test.
            </p>
        </TSIA2Frame>
    );
}

export function TSIA2Saved({ onClose }: { onClose: () => void }) {
    return (
        <TSIA2Frame barTitle='Save and Finish Later' heading='Instructions' primaryLabel='Close' onPrimary={onClose}>
            <p>You have successfully saved the test session. Please click on the close button.</p>
        </TSIA2Frame>
    );
}

// The end of the last routed stage. It says nothing about performance: the
// score lives in the report, and no screen during the test may hint at it.
export function TSIA2Finished({ onFinish, busy }: { onFinish: () => void; busy: boolean }) {
    return (
        <TSIA2Frame
            barTitle='End of Test'
            heading='Instructions'
            primaryLabel={busy ? 'Submitting…' : 'Submit Test'}
            onPrimary={onFinish}
            primaryDisabled={busy}
        >
            <p>You have answered all of the questions on this test.</p>
            <p>Click <b>Submit Test</b> to finish. You will not be able to return to the questions.</p>
        </TSIA2Frame>
    );
}
